import * as XLSX from "xlsx";
import { HrAttendance } from "../types/HrTypes";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export type LeaveBalanceReportRow = {
  emp_id: number;
  emp_name: string;
  pl: number;
  cl: number;
  holiday: number;
  unpaid_taken: number;
};

export type ReportEmployee = {
  emp_id: number;
  emp_name: string;
};

export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

export function currentYear() {
  return new Date().getFullYear();
}

export function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function dayKey(value?: string | null) {
  return String(value || "").slice(0, 10);
}

export function monthBounds(yearMonth: string, today = todayIso()) {
  const [year, month] = yearMonth.split("-").map(Number);
  if (!year || !month) return null;
  const lastDay = new Date(year, month, 0).getDate();
  const from = `${year}-${pad(month)}-01`;
  const end = `${year}-${pad(month)}-${pad(lastDay)}`;
  if (from > today) return null;
  return {
    year,
    month,
    lastDay,
    from,
    to: end > today ? today : end,
  };
}

export function yearBounds(year: number, today = todayIso()) {
  const from = `${year}-01-01`;
  const end = `${year}-12-31`;
  if (from > today) return null;
  const to = end > today ? today : end;
  const lastMonth = Number(to.slice(5, 7));
  return { year, from, to, lastMonth };
}

function leaveCode(row: HrAttendance) {
  return String(row.leaveType?.code || "").toUpperCase();
}

/** Short label for one attendance day, including the leave type. */
export function attendanceDayLabel(row: HrAttendance) {
  const key = String(row.status || "").toLowerCase();
  const code = leaveCode(row);
  if (key === "present") return "Present";
  if (key === "wfh") return "WFH";
  if (key === "absent") return "Absent";
  if (key === "weekend") return "Week off";
  if (key === "holiday") return "Holiday";
  if (key === "unpaid" || (key === "on_leave" && code === "UL")) return "Unpaid";
  if (key === "half_day") {
    if (code === "HOL") return "Half day (Holiday)";
    if (code === "UL") return "Half day (Unpaid)";
    return code ? `Half day (${code})` : "Half day";
  }
  if (key === "on_leave") {
    if (code === "PL") return "PL";
    if (code === "CL") return "CL";
    if (code === "HOL") return "Holiday";
    if (code === "UL") return "Unpaid";
    return row.leaveType?.name || code || "Leave";
  }
  return key ? key.split("_").join(" ") : "Absent";
}

function roster(employees: ReportEmployee[], rows: HrAttendance[]) {
  const names = new Map<number, string>();
  for (const emp of employees) {
    if (emp.emp_id) names.set(emp.emp_id, emp.emp_name || `Employee ${emp.emp_id}`);
  }
  for (const row of rows) {
    if (!names.has(row.emp_id)) {
      names.set(row.emp_id, row.Employee?.emp_name || `Employee ${row.emp_id}`);
    }
  }
  return [...names.entries()]
    .map(([emp_id, emp_name]) => ({ emp_id, emp_name }))
    .sort((a, b) => a.emp_name.localeCompare(b.emp_name, undefined, { sensitivity: "base" }));
}

function monthBlock(
  year: number,
  month: number,
  lastDay: number,
  people: ReportEmployee[],
  byEmpDate: Map<string, HrAttendance>,
  today: string,
) {
  const title = `${MONTHS[month - 1]} ${year}`;
  const header = [
    "Employee",
    ...Array.from({ length: lastDay }, (_, i) => {
      const day = i + 1;
      const weekday = new Date(year, month - 1, day).toLocaleDateString("en-IN", {
        weekday: "short",
      });
      return `${day} ${weekday}`;
    }),
  ];
  const body = people.map((emp) => {
    const cells: Array<string | number> = [emp.emp_name];
    for (let day = 1; day <= lastDay; day += 1) {
      const date = `${year}-${pad(month)}-${pad(day)}`;
      if (date > today) {
        cells.push("");
        continue;
      }
      const row = byEmpDate.get(`${emp.emp_id}|${date}`);
      cells.push(row ? attendanceDayLabel(row) : "Absent");
    }
    return cells;
  });
  return { title, header, body };
}

export function downloadLeaveBalanceWorkbook(year: number, rows: LeaveBalanceReportRow[]) {
  const sheetRows = rows.map((row) => ({
    Employee: row.emp_name,
    "PL remaining": Number(row.pl) || 0,
    "CL remaining": Number(row.cl) || 0,
    "Holiday remaining": Number(row.holiday) || 0,
    "Unpaid taken": Number(row.unpaid_taken) || 0,
  }));
  const worksheet = XLSX.utils.json_to_sheet(
    sheetRows.length
      ? sheetRows
      : [
          {
            Employee: "",
            "PL remaining": "",
            "CL remaining": "",
            "Holiday remaining": "",
            "Unpaid taken": "",
          },
        ],
  );
  worksheet["!cols"] = [
    { wch: 28 },
    { wch: 16 },
    { wch: 16 },
    { wch: 20 },
    { wch: 16 },
  ];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Leave balances");
  XLSX.writeFile(workbook, `Leave_Balances_${year}.xlsx`);
}

export function downloadAttendanceWorkbook(opts: {
  mode: "month" | "year";
  year: number;
  month?: number;
  employees: ReportEmployee[];
  rows: HrAttendance[];
  today?: string;
}) {
  const today = opts.today || todayIso();
  const people = roster(opts.employees, opts.rows);
  const byEmpDate = new Map<string, HrAttendance>();
  for (const row of opts.rows) {
    byEmpDate.set(`${row.emp_id}|${dayKey(row.attendance_date)}`, row);
  }

  const sections =
    opts.mode === "month"
      ? [
          monthBlock(
            opts.year,
            opts.month || 1,
            new Date(opts.year, opts.month || 1, 0).getDate(),
            people,
            byEmpDate,
            today,
          ),
        ]
      : Array.from({ length: yearBounds(opts.year, today)?.lastMonth || 0 }, (_, i) => {
          const month = i + 1;
          return monthBlock(
            opts.year,
            month,
            new Date(opts.year, month, 0).getDate(),
            people,
            byEmpDate,
            today,
          );
        });

  const aoa: Array<Array<string | number>> = [];
  sections.forEach((section, index) => {
    if (index > 0) aoa.push([]);
    aoa.push([section.title]);
    aoa.push(section.header);
    aoa.push(...section.body);
  });

  const worksheet = XLSX.utils.aoa_to_sheet(aoa.length ? aoa : [["No attendance"]]);
  const width = Math.max(12, ...sections.map((section) => section.header.length));
  worksheet["!cols"] = Array.from({ length: width }, (_, i) => ({
    wch: i === 0 ? 28 : 18,
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
  const fileName =
    opts.mode === "month"
      ? `Attendance_${MONTHS[(opts.month || 1) - 1]}_${opts.year}.xlsx`
      : `Attendance_${opts.year}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}
